import * as React from 'react'

import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import { Tag } from '~/client/src/shared/components/Tag/Tag'

const MAX_VISIBLE_ROWS = 3

// Trigger style for the controls editors. The table uses `cell` (borderless, hover
// affordance); the resource-card Controls tab provides `bordered` (input-style box)
// so the same cells match the card design without threading a prop through each one.
export const EditableTagsCellVariantContext = React.createContext<
  'bordered' | 'cell'
>('cell')

// Display-only mode for the whole cell set (e.g. the resource-card Controls tab for
// users who may not change the resource): tags render without edit/remove affordances and the popover
// editor never opens. Set once at the tab root so no cell needs a readOnly prop.
export const EditableTagsCellReadOnlyContext =
  React.createContext<boolean>(false)

export interface IEditableTag {
  key: React.Key
  label: React.ReactNode
  avatar?: React.ReactNode
  icon?: React.ReactNode
  /** Tag click opens the editor for this item; omit for non-editable tags. */
  onClick?: () => void
  /** Remove ("x"); omit for non-removable tags (e.g. permit-linked closures). */
  onDismiss?: () => void
}

interface IProps {
  isBookable: boolean
  tags: IEditableTag[]
  /** Rendered when bookable and there are no tags (e.g. an "All" label). */
  emptyDisplay?: React.ReactNode
  /**
   * Rendered when bookable, before the tags and always (even with tags present) —
   * for a permanent, non-removable leading chip such as the inherited-hours
   * default. Supersedes `emptyDisplay` when set.
   */
  leadingDisplay?: React.ReactNode
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  cellAffordance: React.ComponentProps<
    typeof PopoverTagPicker
  >['cellAffordance']
  /** See PopoverTagPicker: what the cell edits, e.g. its column name. */
  triggerLabel: string
  popoverClassName?: string
  /** See PopoverTagPicker. Set by an editor that resizes while open. */
  popoverCollisionAvoidance?: React.ComponentProps<
    typeof PopoverTagPicker
  >['popoverCollisionAvoidance']
  /** See PopoverTagPicker. */
  popoverSide?: React.ComponentProps<typeof PopoverTagPicker>['popoverSide']
  popoverAlign?: React.ComponentProps<typeof PopoverTagPicker>['popoverAlign']
  popoverContent: React.ReactNode
  /** How the editor surfaces (popover vs full-panel sheet). See PopoverTagPicker. */
  presentation?: React.ComponentProps<typeof PopoverTagPicker>['presentation']
  /** Sheet height when `presentation="sheet"`. See PopoverTagPicker. */
  sheetHeightMode?: React.ComponentProps<
    typeof PopoverTagPicker
  >['sheetHeightMode']
}

/**
 * The shared skeleton every Controls tag-cell repeats: a `PopoverTagPicker`
 * (cell variant, "Not set" placeholder, +n-more overflow) whose body is gated by
 * `isBookable` (off => "Not Set"), renders `leadingDisplay` (always, before the
 * tags) when set, shows `emptyDisplay` when bookable-but-empty (and no
 * leadingDisplay), and otherwise renders the tags. Each cell supplies its own editor
 * (`popoverContent`) and open/draft logic; this owns only the wrapper + gate +
 * tag rendering (including the click stopPropagation so editing a tag doesn't
 * re-trigger the add affordance).
 */
function EditableTagsCell({
  isBookable,
  tags,
  emptyDisplay,
  leadingDisplay,
  isOpen,
  onOpen,
  onClose,
  cellAffordance,
  triggerLabel,
  popoverClassName,
  popoverCollisionAvoidance,
  popoverSide,
  popoverAlign,
  popoverContent,
  presentation,
  sheetHeightMode,
}: IProps) {
  const variant = React.useContext(EditableTagsCellVariantContext)
  const readOnly = React.useContext(EditableTagsCellReadOnlyContext)

  // Tags render as a flat array (never wrapped in a Fragment) so
  // PopoverTagPicker's React.Children overflow counting stays correct — a
  // Fragment collapses to a single child and breaks the "+n more" math.
  const tagElements = tags.map(tag => (
    <Tag
      key={tag.key}
      avatar={tag.avatar}
      icon={tag.icon}
      onClick={
        readOnly || !tag.onClick
          ? undefined
          : event => {
              event.stopPropagation()
              tag.onClick?.()
            }
      }
      onDismiss={readOnly ? undefined : tag.onDismiss}
    >
      {tag.label}
    </Tag>
  ))

  // leadingDisplay rides PopoverTagPicker's pinned `leading` slot (always visible,
  // never collapsed), so the body carries only the collapsible tags. Empty display
  // shows only when there's no leading chip and no tags.
  const body: React.ReactNode = !isBookable
    ? null
    : !leadingDisplay && tags.length === 0
      ? (emptyDisplay ?? null)
      : tagElements

  return (
    <PopoverTagPicker
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placeholder="Not set"
      triggerLabel={triggerLabel}
      variant={variant}
      cellAffordance={cellAffordance}
      maxVisibleRows={MAX_VISIBLE_ROWS}
      popoverClassName={popoverClassName}
      popoverCollisionAvoidance={popoverCollisionAvoidance}
      popoverSide={popoverSide}
      popoverAlign={popoverAlign}
      popoverContent={popoverContent}
      displayOnly={readOnly}
      presentation={presentation}
      sheetHeightMode={sheetHeightMode}
      leading={isBookable ? leadingDisplay : undefined}
    >
      {body}
    </PopoverTagPicker>
  )
}

export default EditableTagsCell
